import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';

export default function IntegrationSettingsPage() {
  const [integ, setInteg] = useState({ sapEndpoint: '', aisFeedUrl: '', gpsGatewayUrl: '', sapAuthKey: '' });
  const [integInfo, setIntegInfo] = useState(null);
  const [notif, setNotif] = useState({
    twilioKey: '',
    fcmAppId: '',
    triggerBookingEmail: true,
    smsAlertsEnabled: true,
    captainPushEnabled: true,
  });
  const [notifInfo, setNotifInfo] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [savingInteg, setSavingInteg] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);

  const [daily, setDaily] = useState({ enabled: false, sendTime: '07:00', recipients: [] });
  const [dailyMsg, setDailyMsg] = useState(null);
  const [dailyLoaded, setDailyLoaded] = useState(false);
  const [safety, setSafety] = useState({ openingHours: '', openingAsOf: '', lastLostTimeDate: '', branches: [] });
  const [safetyLoaded, setSafetyLoaded] = useState(false);
  const [safetyMsg, setSafetyMsg] = useState(null);
  useEffect(() => {
    api
      .getSafetySettings()
      .then((d) => {
        setSafety({ openingHours: d.openingHours ?? '', openingAsOf: d.openingAsOf || '', lastLostTimeDate: d.lastLostTimeDate || '', branches: d.branches || [] });
        setSafetyLoaded(true);
      })
      .catch(() => setSafetyMsg({ type: 'danger', text: 'Could not load the safety settings — reload the page before making changes.' }));
  }, []);
  const editBranch = (i, patch) => setSafety((v) => ({ ...v, branches: v.branches.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  const saveSafety = async () => {
    setSafetyMsg(null);
    try {
      const d = await api.saveSafetySettings({
        openingHours: safety.openingHours === '' ? null : Number(safety.openingHours),
        openingAsOf: safety.openingAsOf || null,
        lastLostTimeDate: safety.lastLostTimeDate || null,
        branches: safety.branches.map((b) => ({ name: b.name, employees: Number(b.employees) || 0, hoursPerDay: Number(b.hoursPerDay) || 0 })),
      });
      setSafety({ openingHours: d.openingHours ?? '', openingAsOf: d.openingAsOf || '', lastLostTimeDate: d.lastLostTimeDate || '', branches: d.branches || [] });
      setSafetyMsg({ type: 'success', text: 'Safety settings saved.' });
    } catch (e) {
      setSafetyMsg({ type: 'danger', text: e.details?.[0]?.message || e.message || 'Failed to save.' });
    }
  };
  const [testTo, setTestTo] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const sendTest = async () => {
    setDailyMsg(null);
    setTestingEmail(true);
    try {
      const res = await api.sendDailyReportTest(testTo.trim());
      setDailyMsg(
        res.simulated
          ? { type: 'warning', text: 'Email is not configured on this server, so the test was only simulated.' }
          : { type: 'success', text: `Test email sent to ${res.to}.` }
      );
    } catch (e) {
      setDailyMsg({ type: 'danger', text: e.details?.[0]?.message || e.message || 'The test email could not be sent.' });
    } finally {
      setTestingEmail(false);
    }
  };
  useEffect(() => {
    api
      .getDailyReportSettings()
      .then((d) => {
        setDaily(d);
        setDailyLoaded(true);
      })
      // Without the stored list, saving would wipe it: keep Save disabled.
      .catch(() => setDailyMsg({ type: 'danger', text: 'Could not load the recipient list — reload the page before making changes.' }));
  }, []);
  const editRecipient = (i, patch) => setDaily((d) => ({ ...d, recipients: d.recipients.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const saveDaily = async () => {
    setDailyMsg(null);
    try {
      setDaily(await api.saveDailyReportSettings({ enabled: daily.enabled, sendTime: daily.sendTime, companyName: daily.companyName || '', recipients: daily.recipients }));
      setDailyMsg({ type: 'success', text: 'Daily report recipients saved.' });
    } catch (e) {
      setDailyMsg({ type: 'danger', text: e.details?.[0]?.message || e.message || 'Failed to save.' });
    }
  };

  const load = useCallback(async () => {
    const [i, n] = await Promise.all([
      api.getIntegrationSettings(),
      api.getNotificationSettings(),
    ]);
    setIntegInfo(i);
    setInteg({
      sapEndpoint: i.sapEndpoint || '',
      aisFeedUrl: i.aisFeedUrl || '',
      gpsGatewayUrl: i.gpsGatewayUrl || '',
      sapAuthKey: '',
    });
    setNotifInfo(n);
    setNotif({
      twilioKey: '',
      fcmAppId: n.fcmAppId || '',
      triggerBookingEmail: n.triggerBookingEmail ?? true,
      smsAlertsEnabled: n.smsAlertsEnabled ?? true,
      captainPushEnabled: n.captainPushEnabled ?? true,
    });
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load settings.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const testConnection = async () => {
    setTesting(true);
    setPageMsg(null);
    try {
      const res = await api.testIntegrationConnection({
        sapEndpoint: integ.sapEndpoint,
        aisFeedUrl: integ.aisFeedUrl,
        gpsGatewayUrl: integ.gpsGatewayUrl,
      });
      setTestResult(res);
      setPageMsg({
        type: res.allConnected ? 'success' : 'warning',
        text: res.allConnected
          ? 'All connections tested successfully.'
          : 'Some endpoints failed to connect — check the URLs.',
      });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Connection test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const saveIntegrations = async () => {
    setSavingInteg(true);
    setPageMsg(null);
    try {
      const payload = {
        sapEndpoint: integ.sapEndpoint,
        aisFeedUrl: integ.aisFeedUrl,
        gpsGatewayUrl: integ.gpsGatewayUrl,
      };
      if (integ.sapAuthKey.trim()) payload.sapAuthKey = integ.sapAuthKey.trim();
      const res = await api.saveIntegrationSettings(payload);
      setIntegInfo(res);
      setInteg((p) => ({ ...p, sapAuthKey: '' }));
      setPageMsg({ type: 'success', text: 'Credentials encrypted and saved securely.' });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to save credentials.' });
    } finally {
      setSavingInteg(false);
    }
  };

  const saveNotifications = async () => {
    setSavingNotif(true);
    setPageMsg(null);
    try {
      const payload = {
        fcmAppId: notif.fcmAppId,
        triggerBookingEmail: notif.triggerBookingEmail,
        smsAlertsEnabled: notif.smsAlertsEnabled,
        captainPushEnabled: notif.captainPushEnabled,
      };
      if (notif.twilioKey.trim()) payload.twilioKey = notif.twilioKey.trim();
      const res = await api.saveNotificationSettings(payload);
      setNotifInfo(res);
      setNotif((p) => ({ ...p, twilioKey: '' }));
      setPageMsg({ type: 'success', text: 'Notification channels updated.' });
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to save notification channels.' });
    } finally {
      setSavingNotif(false);
    }
  };

  const statusBadge = (s) =>
    s === 'connected' ? (
      <span className="badge bg-success">Connected</span>
    ) : (
      <span className="badge bg-danger">Failed</span>
    );

  return (
    <div className="container-fluid py-4" data-testid="integration-settings-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-server me-2" aria-hidden="true" />
                External Service Integrations &amp; Settings
              </h3>
              <p className="text-muted mb-0 small">
                Manage secure credentials and endpoints for SAP, AIS and GPS services, and configure
                notification channels.
              </p>
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="settings-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="settings-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* API integration settings */}
          <div className="col-lg-7 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-plug me-2" aria-hidden="true" />
                  API Integration Settings
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="sap-endpoint" className="form-label small fw-bold text-muted">
                    SAP S/4HANA OData Service Endpoint <span className="text-danger">*</span>
                  </label>
                  <input
                    id="sap-endpoint"
                    type="url"
                    className="form-control"
                    value={integ.sapEndpoint}
                    onChange={(e) => setInteg((p) => ({ ...p, sapEndpoint: e.target.value }))}
                    data-testid="sap-endpoint"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="sap-key" className="form-label small fw-bold text-muted">
                    SAP API Authentication Key <span className="text-danger">*</span>
                  </label>
                  <input
                    id="sap-key"
                    type="password"
                    className="form-control"
                    value={integ.sapAuthKey}
                    placeholder={integInfo?.hasSapAuthKey ? `Saved (${integInfo.sapAuthKeyMasked})` : 'Enter key'}
                    onChange={(e) => setInteg((p) => ({ ...p, sapAuthKey: e.target.value }))}
                    data-testid="sap-key"
                  />
                  <div className="form-text">Stored encrypted — leave blank to keep the existing key.</div>
                </div>
                <div className="mb-3">
                  <label htmlFor="ais-url" className="form-label small fw-bold text-muted">
                    VesselFinder AIS Feed URL <span className="text-danger">*</span>
                  </label>
                  <input
                    id="ais-url"
                    type="url"
                    className="form-control"
                    value={integ.aisFeedUrl}
                    onChange={(e) => setInteg((p) => ({ ...p, aisFeedUrl: e.target.value }))}
                    data-testid="ais-url"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="gps-url" className="form-label small fw-bold text-muted">
                    GPS Tracking Gateway Endpoint <span className="text-danger">*</span>
                  </label>
                  <input
                    id="gps-url"
                    type="url"
                    className="form-control"
                    value={integ.gpsGatewayUrl}
                    onChange={(e) => setInteg((p) => ({ ...p, gpsGatewayUrl: e.target.value }))}
                    data-testid="gps-url"
                  />
                </div>

                {testResult && (
                  <div className="border rounded p-2 mb-3 small" data-testid="test-result">
                    {[
                      ['sap', 'SAP S/4HANA'],
                      ['ais', 'VesselFinder AIS'],
                      ['gps', 'GPS Gateway'],
                    ].map(([svc, label], i, all) => (
                      <div key={svc} className={i < all.length - 1 ? 'mb-1' : ''}>
                        <div className="d-flex justify-content-between">
                          <span>{label}</span>
                          <span data-testid={`status-${svc}`}>{statusBadge(testResult.services[svc])}</span>
                        </div>
                        {testResult.errors?.[svc] && (
                          <div className="text-danger" data-testid={`status-${svc}-error`}>
                            {testResult.errors[svc]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={testConnection}
                    disabled={testing}
                    data-testid="test-connection"
                  >
                    {testing && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    <i className="fas fa-vial me-1" aria-hidden="true" />
                    Test Connection
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={saveIntegrations}
                    disabled={savingInteg}
                    data-testid="save-credentials"
                  >
                    {savingInteg && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    <i className="fas fa-lock me-1" aria-hidden="true" />
                    Save Credentials
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Notification settings */}
          <div className="col-lg-5 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-bell me-2" aria-hidden="true" />
                  Multi-channel Notification Settings
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="twilio-key" className="form-label small fw-bold text-muted">
                    Twilio SMS Gateway Key
                  </label>
                  <input
                    id="twilio-key"
                    type="password"
                    className="form-control"
                    value={notif.twilioKey}
                    placeholder={notifInfo?.hasTwilioKey ? `Saved (${notifInfo.twilioKeyMasked})` : 'Enter key'}
                    onChange={(e) => setNotif((p) => ({ ...p, twilioKey: e.target.value }))}
                    data-testid="twilio-key"
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="fcm-id" className="form-label small fw-bold text-muted">
                    FCM Push Notification App ID
                  </label>
                  <input
                    id="fcm-id"
                    className="form-control"
                    value={notif.fcmAppId}
                    onChange={(e) => setNotif((p) => ({ ...p, fcmAppId: e.target.value }))}
                    data-testid="fcm-id"
                  />
                </div>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="trigger-email"
                    checked={notif.triggerBookingEmail}
                    onChange={(e) => setNotif((p) => ({ ...p, triggerBookingEmail: e.target.checked }))}
                    data-testid="trigger-email"
                  />
                  <label className="form-check-label small" htmlFor="trigger-email">
                    Trigger customer SMS/email booking reminders on AIS approach (geofenced)
                  </label>
                </div>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="sms-alerts"
                    checked={notif.smsAlertsEnabled}
                    onChange={(e) => setNotif((p) => ({ ...p, smsAlertsEnabled: e.target.checked }))}
                    data-testid="sms-alerts"
                  />
                  <label className="form-check-label small" htmlFor="sms-alerts">
                    SMS dispatcher if crew ticket acknowledgement exceeds the 10-min SLA
                  </label>
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="captain-push"
                    checked={notif.captainPushEnabled}
                    onChange={(e) => setNotif((p) => ({ ...p, captainPushEnabled: e.target.checked }))}
                    data-testid="captain-push"
                  />
                  <label className="form-check-label small" htmlFor="captain-push">
                    Immediate captain push alarm on dispatch job issuance
                  </label>
                </div>
                <button
                  className="btn btn-primary w-100"
                  onClick={saveNotifications}
                  disabled={savingNotif}
                  data-testid="save-notifications"
                >
                  {savingNotif && (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  )}
                  Save Notification Channels
                </button>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card border-0 shadow-sm mb-4" data-testid="daily-report-card">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary"><i className="fas fa-envelope-open-text me-2" aria-hidden="true" />Daily Report</h5>
              </div>
              <div className="card-body">
                {dailyMsg && <div className={`alert alert-${dailyMsg.type} py-2`} role="alert" data-testid="daily-report-message">{dailyMsg.text}</div>}
                <div className="row g-3 align-items-end mb-3">
                  <div className="col-md-4 form-check form-switch ms-3">
                    <input id="daily-enabled" type="checkbox" className="form-check-input" checked={daily.enabled} onChange={(e) => setDaily({ ...daily, enabled: e.target.checked })} data-testid="daily-report-enabled" />
                    <label htmlFor="daily-enabled" className="form-check-label">Send the daily report</label>
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="daily-time" className="form-label small fw-bold text-muted">Send time (Manila)</label>
                    <input id="daily-time" type="time" className="form-control form-control-sm" value={daily.sendTime} onChange={(e) => setDaily({ ...daily, sendTime: e.target.value })} data-testid="daily-report-time" />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="daily-company" className="form-label small fw-bold text-muted">Company name (email header)</label>
                    <input id="daily-company" className="form-control form-control-sm" maxLength={120} value={daily.companyName || ''} onChange={(e) => setDaily({ ...daily, companyName: e.target.value })} data-testid="daily-report-company" />
                  </div>
                </div>
                {daily.recipients.map((r, i) => (
                  <div className="row g-2 align-items-center mb-2" key={i} data-testid="recipient-row">
                    <div className="col-md-3"><input className="form-control form-control-sm" placeholder="Name" aria-label="Recipient name" value={r.name} onChange={(e) => editRecipient(i, { name: e.target.value })} data-testid="recipient-name" /></div>
                    <div className="col-md-5"><input type="email" className="form-control form-control-sm" placeholder="Email" aria-label="Recipient email" value={r.email} onChange={(e) => editRecipient(i, { email: e.target.value })} data-testid="recipient-email" /></div>
                    <div className="col-md-2 form-check form-switch">
                      <input id={`recipient-active-${i}`} type="checkbox" className="form-check-input" checked={r.active} onChange={(e) => editRecipient(i, { active: e.target.checked })} data-testid="recipient-active" />
                      <label htmlFor={`recipient-active-${i}`} className="form-check-label small">Active</label>
                    </div>
                    <div className="col-md-2"><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setDaily((d) => ({ ...d, recipients: d.recipients.filter((_x, idx) => idx !== i) }))} data-testid="recipient-remove">Remove</button></div>
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => setDaily((d) => ({ ...d, recipients: [...d.recipients, { name: '', email: '', active: true }] }))} data-testid="add-recipient">Add recipient</button>
                <button type="button" className="btn btn-sm btn-primary" onClick={saveDaily} disabled={!dailyLoaded} data-testid="save-daily-report">Save</button>
                <hr />
    <div className="row g-2 align-items-end">
      <div className="col-md-6">
        <label htmlFor="daily-test-to" className="form-label small fw-bold text-muted">
          Send a test email to
        </label>
        <input id="daily-test-to" type="email" className="form-control form-control-sm" placeholder="name@company.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="daily-test-to" />
      </div>
      <div className="col-md-6">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={sendTest} disabled={testingEmail || !testTo.trim()} data-testid="send-daily-test">
          Send test email
        </button>
        <span className={`small ms-2 ${daily.emailConfigured ? 'text-success' : 'text-muted'}`} data-testid="daily-email-status">
          {daily.emailConfigured ? 'Outgoing email is configured.' : 'Outgoing email is not configured on this server.'}
        </span>
      </div>
    </div>
    <p className="small text-muted mt-3 mb-0" data-testid="daily-last-run">
      {daily.lastRun
        ? `Last send: ${new Date(daily.lastRun.attemptedAt).toLocaleString('en-US', { timeZone: 'Asia/Manila', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} (Manila) — ${daily.lastRun.status}, ${daily.lastRun.recipientCount} recipient${daily.lastRun.recipientCount === 1 ? '' : 's'}${daily.lastRun.error ? ` — ${daily.lastRun.error}` : ''}`
        : 'The report covering the previous day is emailed at the send time above. Nothing has been sent yet.'}
    </p>
              </div>
            </div>
          </div>

          <div className="col-12 mt-4">
            <div className="card border-0 shadow-sm mb-4" data-testid="safety-card">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary"><i className="fas fa-shield-halved me-2" aria-hidden="true" />Safety Man-Hours</h5>
              </div>
              <div className="card-body">
                {safetyMsg && <div className={`alert alert-${safetyMsg.type} py-2`} role="alert" data-testid="safety-message">{safetyMsg.text}</div>}
                <p className="small text-muted">
                  Safe man-hours are worked out each day from crew on board. Enter the total carried over from before TugOS and the day it is correct as of; TugOS adds every day after that.
                </p>
                <div className="row g-3 align-items-end mb-3">
                  <div className="col-md-4">
                    <label htmlFor="safety-opening" className="form-label small fw-bold text-muted">Accumulated safe man-hours</label>
                    <input id="safety-opening" type="number" min="0" className="form-control form-control-sm" value={safety.openingHours} onChange={(e) => setSafety({ ...safety, openingHours: e.target.value })} data-testid="safety-opening" />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="safety-asof" className="form-label small fw-bold text-muted">Correct as of</label>
                    <input id="safety-asof" type="date" className="form-control form-control-sm" value={safety.openingAsOf} onChange={(e) => setSafety({ ...safety, openingAsOf: e.target.value })} data-testid="safety-asof" />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="safety-lta" className="form-label small fw-bold text-muted">Last lost-time accident</label>
                    <input id="safety-lta" type="date" className="form-control form-control-sm" value={safety.lastLostTimeDate} onChange={(e) => setSafety({ ...safety, lastLostTimeDate: e.target.value })} data-testid="safety-lta" />
                  </div>
                </div>
                <div className="small fw-bold text-muted mb-2">Office branches counted (optional)</div>
                {safety.branches.map((b, i) => (
                  <div className="row g-2 align-items-center mb-2" key={i} data-testid="branch-row">
                    <div className="col-md-5"><input className="form-control form-control-sm" placeholder="Branch" aria-label="Branch name" value={b.name} onChange={(e) => editBranch(i, { name: e.target.value })} data-testid="branch-name" /></div>
                    <div className="col-md-2"><input type="number" min="0" className="form-control form-control-sm" placeholder="Employees" aria-label="Employees" value={b.employees} onChange={(e) => editBranch(i, { employees: e.target.value })} data-testid="branch-employees" /></div>
                    <div className="col-md-3"><input type="number" min="0" max="24" step="0.5" className="form-control form-control-sm" placeholder="Hours per day" aria-label="Hours per day" value={b.hoursPerDay} onChange={(e) => editBranch(i, { hoursPerDay: e.target.value })} data-testid="branch-hours" /></div>
                    <div className="col-md-2"><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setSafety((v) => ({ ...v, branches: v.branches.filter((_x, idx) => idx !== i) }))}>Remove</button></div>
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => setSafety((v) => ({ ...v, branches: [...v.branches, { name: '', employees: '', hoursPerDay: 9 }] }))} data-testid="add-branch">Add branch</button>
                <button type="button" className="btn btn-sm btn-primary" onClick={saveSafety} disabled={!safetyLoaded} data-testid="save-safety">Save</button>
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
